import React from 'react';

function FleetDistribution({ rank, total, percentage, benchmark, acFamily, acType, fleetAvg, fleetStd }) {
  if (!rank || !total) return null;

  const avg     = fleetAvg ?? (benchmark ? parseFloat(benchmark.avg_percentage) : 70);
  const std     = fleetStd ?? 12;
  const label   = acFamily || acType || 'Fleet';
  const topPct  = Math.round((rank / total) * 100);
  const isTop   = topPct <= 50;
  const pctText = isTop ? `Top ${topPct}%` : `Bottom ${100 - topPct}%`;

  // Colors — same tier system as grade badges
  const badgeBg     = percentage >= 80 ? '#bbf7e0' : percentage >= 50 ? '#bfdbfe' : '#fef08a';
  const badgeBorder = percentage >= 80 ? '#10b981' : percentage >= 50 ? '#3b82f6' : '#ca8a04';
  const badgeText   = percentage >= 80 ? '#065f46' : percentage >= 50 ? '#1e3a8a' : '#713f12';
  const fillColor   = percentage >= 80 ? '#10b981' : percentage >= 50 ? '#3b82f6' : '#f59e0b';

  const W = 300, H = 80;
  const ml = 10, mr = 10, mt = 12, mb = 20;
  const pw = W - ml - mr, ph = H - mt - mb;

  const pdf = x => Math.exp(-0.5 * ((x - avg) / std) ** 2);

  const xMin = Math.max(0,   avg - 3.8 * std);
  const xMax = Math.min(100, avg + 3.8 * std);
  const xToSvg = x => ml + ((x - xMin) / (xMax - xMin)) * pw;

  const N = 140;
  const pts = Array.from({ length: N + 1 }, (_, i) => ({
    x: xMin + (i / N) * (xMax - xMin),
    y: pdf(xMin + (i / N) * (xMax - xMin)),
  }));
  const maxY = Math.max(...pts.map(p => p.y));
  const yToSvg = y => mt + ph * (1 - y / maxY);
  const baseY = yToSvg(0);

  const curvePts = pts.map(p =>
    `${xToSvg(p.x).toFixed(1)},${yToSvg(p.y).toFixed(1)}`
  ).join(' ');

  // Fill LEFT of marker = flights this pilot beats
  const scoreClamped = Math.min(Math.max(percentage, xMin), xMax);
  const fillLeft = pts.filter(p => p.x <= scoreClamped);
  const fillPath = fillLeft.length > 1
    ? `M ${xToSvg(fillLeft[0].x).toFixed(1)},${baseY.toFixed(1)} `
      + fillLeft.map(p => `L ${xToSvg(p.x).toFixed(1)},${yToSvg(p.y).toFixed(1)}`).join(' ')
      + ` L ${xToSvg(scoreClamped).toFixed(1)},${baseY.toFixed(1)} Z`
    : '';

  const flightX = xToSvg(scoreClamped);
  const flightY = yToSvg(pdf(scoreClamped));

  return (
    <div style={{ marginTop: 16 }}>
      <div className="section-title">{label} Distribution — Last 30 Days</div>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>

          {/* Fill: region pilot beats (left of marker) */}
          {fillPath && <path d={fillPath} fill={fillColor} opacity="0.2" />}

          {/* Bell curve */}
          <polyline points={curvePts} fill="none" stroke={fillColor} strokeWidth="2" opacity="0.7" />

          {/* Avg dashed line */}
          <line
            x1={xToSvg(avg).toFixed(1)} y1={mt}
            x2={xToSvg(avg).toFixed(1)} y2={baseY.toFixed(1)}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3,2" opacity="0.4"
          />
          <text x={xToSvg(avg).toFixed(1)} y={mt + 9}
            textAnchor="middle" fill="var(--text-muted)" fontSize="8" fontFamily="var(--font-mono)" opacity="0.6">
            avg
          </text>

          {/* Flight marker line + dot */}
          <line
            x1={flightX.toFixed(1)} y1={mt}
            x2={flightX.toFixed(1)} y2={baseY.toFixed(1)}
            stroke={fillColor} strokeWidth="2"
          />
          <circle cx={flightX.toFixed(1)} cy={flightY.toFixed(1)} r="4"
            fill={fillColor} stroke="var(--bg-card)" strokeWidth="1.5" />

          {/* Score label next to marker */}
          <text
            x={(flightX + 5).toFixed(1)} y={(flightY + 4).toFixed(1)}
            textAnchor="start" fill={fillColor} fontSize="9" fontFamily="var(--font-mono)" fontWeight="700">
            {percentage}%
          </text>

          {/* X axis ticks */}
          {[xMin, avg, xMax].map((v, i) => (
            <text key={i}
              x={xToSvg(v).toFixed(1)} y={H - 3}
              textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
              fill="var(--text-muted)" fontSize="8" fontFamily="var(--font-mono)">
              {Math.round(v)}%
            </text>
          ))}

          {/* Quartile badge — top left corner of SVG */}
          <rect x="0" y="0" width="82" height="22" rx="6"
            fill={badgeBg} stroke={badgeBorder} strokeWidth="1.5" />
          <text x="41" y="14.5" textAnchor="middle"
            fill={badgeText} fontSize="11" fontFamily="var(--font-mono)" fontWeight="800">
            {pctText}
          </text>

        </svg>
      </div>
    </div>
  );
}

export { FleetDistribution };
